import React from 'react';
import Card from './Card';

interface PartnerCardProps {
  name: string;
  description: string;
  discordLink: string;
  color: 'blue' | 'red' | 'green' | 'purple' | 'yellow' | 'orange';
  icon?: string;
  createdDate?: string;
}

const PartnerCard: React.FC<PartnerCardProps> = ({ 
  name, 
  description, 
  discordLink, 
  color,
  icon,
  createdDate = ''
}) => {
  const colorClasses = {
    blue: {
      bg: 'from-blue-600 to-blue-800',
      button: 'bg-blue-600 hover:bg-blue-700',
      border: 'border-blue-500/50'
    },
    red: {
      bg: 'from-red-600 to-red-800',
      button: 'bg-red-600 hover:bg-red-700',
      border: 'border-red-500/50'
    },
    green: {
      bg: 'from-green-600 to-green-800',
      button: 'bg-green-600 hover:bg-green-700',
      border: 'border-green-500/50'
    },
    purple: {
      bg: 'from-purple-600 to-purple-800',
      button: 'bg-purple-600 hover:bg-purple-700',
      border: 'border-purple-500/50'
    },
    yellow: {
      bg: 'from-yellow-600 to-yellow-800',
      button: 'bg-yellow-600 hover:bg-yellow-700',
      border: 'border-yellow-500/50'
    },
    orange: {
      bg: 'from-orange-600 to-orange-800',
      button: 'bg-orange-600 hover:bg-orange-700',
      border: 'border-orange-500/50'
    }
  };

  const classes = colorClasses[color];

  return (
    <Card hover className={`overflow-hidden border-2 ${classes.border}`}>
      {/* Header avec gradient comme Discord */}
      <div className={`h-16 bg-gradient-to-r ${classes.bg} relative`}>
        <div className="absolute inset-0 bg-black/20"></div>
      </div>
      
      {/* Contenu principal */}
      <div className="p-4">
        {/* Icône : toujours le logo Discord */}
        <div className="flex items-center mb-3">
          <div className="w-12 h-12 bg-gray-700/50 rounded-full flex items-center justify-center mr-3 border-2 border-gray-600/50">
            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
            </svg>
          </div>
          <div>
            <h3 className="text-white font-semibold text-lg">{name}</h3>
            <p className="text-gray-400 text-sm">{description}</p>
          </div>
        </div>

        {/* Date de création */}
        {createdDate && (
          <div className="mb-4">
            <div className="text-sm text-gray-400">
              depuis {createdDate}
            </div>
          </div>
        )}

        {/* Bouton d'action */}
        <a 
          href={discordLink} 
          target="_blank" 
          rel="noopener noreferrer"
          className={`w-full inline-flex items-center justify-center ${classes.button} text-white px-4 py-2 rounded-md font-medium transition-all duration-300 hover:shadow-lg hover:scale-105`}
        >
          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
          </svg>
          Aller sur le serveur
        </a>
      </div>
    </Card>
  );
};

export default PartnerCard;

